/**
 * Synthetic placeholder representing a missing time bucket in an aggregated
 * series. Carries only the time range (Unix-epoch nanoseconds); no value
 * field. Use the `"value" in x` type guard at the use site to distinguish
 * real records from placeholders.
 */
export interface BucketPlaceholder {
  start_time: number;
  end_time: number;
}

/**
 * Insert synthetic placeholder entries for any missing time buckets between
 * consecutive real records.
 *
 * Gap size is measured against each record's own duration, so no period
 * arithmetic is needed — the function works for daily/weekly/monthly/quarterly
 * /yearly aggregates whose individual buckets have stable durations (within
 * the gap they bracket). All values are Unix-epoch nanoseconds.
 */
export function fillMissingBuckets<T extends BucketPlaceholder>(
  data: T[],
): (T | BucketPlaceholder)[] {
  if (data.length < 2) {
    return data;
  }

  const sorted = [...data].sort((a, b) => a.start_time - b.start_time);

  const result: (T | BucketPlaceholder)[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];

    const prevDurationNs = prev.end_time - prev.start_time;
    const gapNs = curr.start_time - prev.end_time;

    const missingCount = Math.round(gapNs / prevDurationNs);
    for (let j = 0; j < missingCount; j++) {
      const bucketStartNs = prev.end_time + j * prevDurationNs;
      result.push({
        start_time: bucketStartNs,
        end_time: bucketStartNs + prevDurationNs,
      });
    }

    result.push(curr);
  }

  return result;
}
